// v1.0.0 - 2026-02-01 - Energy line chart: immessa vs prelevata vs EACI

import { useMemo } from 'react'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'

interface HourlyData {
  date: string // YYYY-MM-DD
  hour: number // 1-24
  immessa: number
  prelevata: number
  eaci: number
}

interface EnergyChartProps {
  data: HourlyData[]
  title?: string
  description?: string
  timeRange?: 'hourly' | 'daily' | 'monthly'
}

export function EnergyChart({
  data,
  title = 'Andamento Energia',
  description,
  timeRange = 'daily',
}: EnergyChartProps) {
  const chartData = useMemo(() => {
    if (timeRange === 'hourly') {
      return data.map((d) => ({
        label: `${d.date} H${d.hour}`,
        immessa: d.immessa,
        prelevata: d.prelevata,
        eaci: d.eaci,
      }))
    }

    if (timeRange === 'daily') {
      const byDay = new Map<string, { immessa: number; prelevata: number; eaci: number }>()

      for (const d of data) {
        if (!byDay.has(d.date)) {
          byDay.set(d.date, { immessa: 0, prelevata: 0, eaci: 0 })
        }
        const entry = byDay.get(d.date)!
        entry.immessa += d.immessa
        entry.prelevata += d.prelevata
        entry.eaci += d.eaci
      }

      return Array.from(byDay.entries())
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([date, values]) => ({
          label: new Date(date).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' }),
          ...values,
        }))
    }

    // monthly
    const byMonth = new Map<string, { immessa: number; prelevata: number; eaci: number }>()

    for (const d of data) {
      const monthKey = d.date.slice(0, 7) // YYYY-MM
      if (!byMonth.has(monthKey)) {
        byMonth.set(monthKey, { immessa: 0, prelevata: 0, eaci: 0 })
      }
      const entry = byMonth.get(monthKey)!
      entry.immessa += d.immessa
      entry.prelevata += d.prelevata
      entry.eaci += d.eaci
    }

    const monthNames = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic']

    return Array.from(byMonth.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([monthKey, values]) => {
        const [, month] = monthKey.split('-')
        return {
          label: monthNames[parseInt(month, 10) - 1],
          ...values,
        }
      })
  }, [data, timeRange])

  const formatValue = (value: number) => {
    if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}k`
    }
    return value.toFixed(1)
  }

  if (data.length === 0) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center h-64 text-[var(--color-text-secondary)]">
          Nessun dato disponibile
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      <CardContent>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis
                dataKey="label"
                stroke="var(--color-text-secondary)"
                fontSize={12}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                stroke="var(--color-text-secondary)"
                fontSize={12}
                tickLine={false}
                axisLine={false}
                tickFormatter={formatValue}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'var(--color-bg-primary)',
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px',
                }}
                formatter={(value: number) => [`${value.toFixed(2)} kWh`, '']}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="immessa"
                name="Immessa"
                stroke="#22c55e"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
              />
              <Line
                type="monotone"
                dataKey="prelevata"
                name="Prelevata"
                stroke="#3b82f6"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
              />
              <Line
                type="monotone"
                dataKey="eaci"
                name="EACI"
                stroke="#f59e0b"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  )
}
